import { Autocomplete, GoogleMap, Marker, useLoadScript } from "@react-google-maps/api";
import React, { useState } from "react"
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import Select from "react-select";
//import "./sellForm.css";

export enum ESellFormLayout {
  column2 = "column2",
  column3map = "column3map",
  row2map = "row2map",
}

export interface IPropertyTypeInfo {
  type: string,
  subtypes?: string[],
}

export interface ISellFormSelectProps {
  placeholder: string,
  selectOptions: { label: string, value: string }[]
}

export interface ISellFormProps {
  formId?: string,
  layout: ESellFormLayout,
  classNames?: string
  children?: React.ReactNode;
  title?: string,
  propertyTypes: IPropertyTypeInfo[],
  propertyTypeSelectLabel?: string,
  propertySubtypeSelectLabel?: string,
  bedsSelect?: ISellFormSelectProps,
  bathsSelect?: ISellFormSelectProps,
  squareFootageSelect?: ISellFormSelectProps,
  conditionSelect?: ISellFormSelectProps,
  inputTextAreaLabel?: string,
  mapApiKey?: string,
  mapDefaultCenter?: { lat: number, lng: number }
  clickHandler?: (formValue?: any) => any,
  showForm?: boolean;
}

export const SellForm = ({
  formId = "sellForm",
  layout = ESellFormLayout.column2,
  classNames = "",
  children,
  title = "Sell With Us",
  propertyTypes,
  propertyTypeSelectLabel = "Property Type",
  propertySubtypeSelectLabel = "Sub-Type",
  bedsSelect = { placeholder: "Beds", selectOptions: [] },
  bathsSelect = { placeholder: "Baths", selectOptions: [] },
  conditionSelect = { placeholder: "Condition", selectOptions: [] },
  squareFootageSelect = { placeholder: "Sq. Ft.", selectOptions: [] },
  inputTextAreaLabel = "",
  mapApiKey = "AIzaSyBSypNhQTAdXM5ua2lx93n2Cwso-Rgra4w",
  mapDefaultCenter,
  clickHandler,
  showForm = true,
}: ISellFormProps) => {

  if(!showForm) {
    return <></>
  }

  const [isSendingRequest, setisSendingRequest] = useState(false)
  const [isRequestCompleted, setisRequestCompleted] = useState(false)

  type ISelectOptionType = {
    value: string;
    label: string;
  }

  type Libraries = ("drawing" | "geometry" | "localContext" | "places" | "visualization")[];
  const [libraries] = useState<Libraries>(["places"])
  const { executeRecaptcha } = useGoogleReCaptcha();
  const inputClassBase = `border-body-text placeholder-body-text leading-10 w-full rounded-none`;

  let locationInput = (
    <input
      type="text"
      name="location"
      id="location"
      placeholder="LOCATION"
      required
      className={`${inputClassBase} border px-2 bg-transparent`}
    />
  )
  let formMap = <></>

  const defaultCenter: any = mapDefaultCenter ? mapDefaultCenter : null
  const [center, setCenter] = useState(defaultCenter)
  if (mapApiKey) {

    const { isLoaded } = useLoadScript({
      googleMapsApiKey: mapApiKey,
      libraries,
    });

    const defaultAutocomplete: any = null
    const [loadedAutocomplete, setAutocomplete] = useState(defaultAutocomplete)

    const changeLocationHandler = () => {
      if (loadedAutocomplete != null) {
        let location = loadedAutocomplete.getPlace()?.geometry?.location ?? null
        let newLat = location?.lat() ?? 0
        let newLng = location?.lng() ?? 0
        setCenter({ lat: newLat, lng: newLng })
      }
    }
    const loadAutocompleteHandler = (autocomplete: any) => {
      setAutocomplete(autocomplete)
    }

    locationInput = isLoaded ?
      <Autocomplete
        restrictions={{ country: "us" }}
        onLoad={loadAutocompleteHandler}
        onPlaceChanged={() => { changeLocationHandler() }}
      >
        {locationInput}
      </Autocomplete>
      : <></>

    formMap = isLoaded ?
      <GoogleMap
        mapContainerStyle={{
          width: "100%",
          height: "100%",
        }}
        center={center}
        zoom={12}
      >
        <Marker
          position={center}
        >
        </Marker>
      </GoogleMap> : <></>
  }

  const propertyTypeSelectOptions: ISelectOptionType[] = propertyTypes.map((item) => ({ label: item.type, value: item.type }))
  const [propertyType, setPropertyType] = useState<ISelectOptionType>()
  const [subTypesOptions, setSubTypesOptions] = useState<ISelectOptionType[]>([])
  const changePropertyTypeHandler = (option: any) => {
    setPropertyType(option)
    setSubType(null)
    let subTypesArray = propertyTypes.filter((item) => {
      return item.type == option.value
    })

    if (subTypesArray && subTypesArray[0].subtypes) {
      const options: ISelectOptionType[] = subTypesArray[0].subtypes.map((subtype, index) => ({
        value: subtype,
        label: subtype,
      }))

      setSubTypesOptions(options)
    }
    else {
      setSubTypesOptions([])
    }
  }

  const [subtype, setSubType] = useState<ISelectOptionType | null>(null)
  const changePropertySubtypeHandler = (option: any) => {
    setSubType(option)
  }

  const [bedsCount, setBedsCount] = useState<ISelectOptionType | null>(null)
  const changeBedsHandler = (option: any) => {
    setBedsCount(option)
  }

  const [bathsCount, setBathsCount] = useState<ISelectOptionType | null>(null)
  const changeBathsHandler = (option: any) => {
    setBathsCount(option)
  }

  const [squareFootage, setSquareFootage] = useState<ISelectOptionType | null>(null)
  const changeSqFootageHandler = (option: any) => {
    setSquareFootage(option)
  }

  const [condition, setCondition] = useState<ISelectOptionType | null>(null)
  const changeConditionHandler = (option: any) => {
    setCondition(option)
  }

  const selectStyles: any = {
    control: (base: object, state: any) => ({
      ...base,
      background: "transparent",
      borderColor: "var(--color-body-text)",
      borderRadius: 0,
      paddingLeft: "0.5rem",
      paddingRight: "0.5rem",
    }),
    singleValue: (base: object) => ({
      ...base,
      color: "var(--color-body-text)",
      lineHeight: "2.5rem",
      textTransform: "uppercase",
    }),
    menu: (base: object) => ({
      ...base,
      color: "var(--color-body-text)",
      textTransform: "uppercase",
    }),
    dropdownIndicator: (base: object) => ({
      ...base,
      paddingRight: "0px",
      color: "var(--color-body-text)",
    }),
    indicatorSeparator: (base: object) => ({
      ...base,
      display: "none",
    }),
    placeholder: (base: object) => ({
      ...base,
      color: "var(--color-body-text)",
      whiteSpace: "nowrap",
      lineHeight: "2.5rem",
      textTransform: "uppercase",
    }),
    valueContainer: (base: object) => ({
      ...base,
      padding: 0,
    }),
  }

  let mapDimensionBase = "w-full h-[50vh] md:h-[30vh]"
  let layoutVariant = {
    mainListGridConfig: "",
    mapDimension: "",
    displayMap: false,
    gridItemsConfig: {
      firstName: "order-1 col-span-2 md:col-span-1",
      lastName: "order-2 col-span-2 md:col-span-1",
      email: "order-3 lg:order-4 col-span-2 md:col-span-1",
      phone: "order-4 lg:order-5 col-span-2 md:col-span-1",
      textArea: "order-5 lg:order-8 col-span-2 lg:row-span-2",
      location: "order-6 lg:order-3 col-span-2",
      propertyType: "order-7 lg:order-6 col-span-2 md:col-span-1",
      propertySubType: "order-8 lg:order-7 col-span-2 md:col-span-1",
      beds: "order-9",
      baths: "order-10",
      sqft: "order-11",
      condition: "order-12",
      map: "order-[13] col-span-2 lg:col-span-4",
      submit: "order-[14] col-span-2 lg:col-start-2",
    },
  }
  switch (layout) {
    case ESellFormLayout.column2:
      layoutVariant = {
        ...layoutVariant,
        mainListGridConfig: "grid-cols-2 lg:grid-cols-4 gap-4",
      }
      break
    case ESellFormLayout.column3map:
      layoutVariant = {
        ...layoutVariant,
        mainListGridConfig: "grid-cols-2 lg:grid-cols-6 gap-4",
        mapDimension: `${mapDimensionBase} lg:h-auto`,
        displayMap: true,
        gridItemsConfig: {
          firstName: "order-1 col-span-2 md:col-span-1",
          lastName: "order-2 col-span-2 md:col-span-1",
          email: "order-3 lg:order-5 col-span-2 md:col-span-1",
          phone: "order-4 lg:order-6 col-span-2 md:col-span-1",
          textArea: "order-5 lg:order-9 col-span-2 lg:row-span-2",
          location: "order-6 lg:order-3 col-span-2",
          propertyType: "order-7 lg:order-7 col-span-2 md:col-span-1",
          propertySubType: "order-8 lg:order-8 col-span-2 md:col-span-1",
          beds: "order-9 lg:order-10",
          baths: "order-10 lg:order-11",
          sqft: "order-11 lg:order-12",
          condition: "order-12 lg:order-[13]",
          map: "order-[13] lg:order-4 col-span-2 lg:col-span-2 lg:row-span-4",
          submit: "order-[14] col-span-2 lg:col-start-5",
        },
      }
      break
    case ESellFormLayout.row2map:
      layoutVariant = {
        ...layoutVariant,
        mainListGridConfig: "grid-cols-2 lg:grid-cols-4 gap-4",
        mapDimension: `${mapDimensionBase}`,
        displayMap: true,
      }
      break
  }

  return isRequestCompleted ? (
    <fieldset
      className={`px-2 pb-2 lg:px-10 lg:pb-10 border-divider`}
    >
      <legend className={`text-xl font-bold text-center pt-10`}>
        Your Information Was Sent To Us...
      </legend>
    </fieldset>
  ) : (
    <form
      id={formId}
      className={`sell-form ${layout} ${classNames}`}
      onSubmit={async (e: React.SyntheticEvent) => {
        e.preventDefault();

        const target: any = e.target;

        let formValues: any = {
          firstName: target.firstname.value,
          lastName: target.lastname.value,
          email: target.email.value,
          phone: target.phone.value,
          textarea: target.textarea.value,
          location: target.location.value,
          propertytype: target.propertytype.value,
          propertysubtype: target.propertysubtype?.value,
          beds: target.beds.value,
          baths: target.baths.value,
          sqft: target.sqft.value,
          condition: target.condition.value,
        };

        setisSendingRequest(true)

        clickHandler && await clickHandler(formValues);

        setisSendingRequest(false)
        setisRequestCompleted(true)
      }}
    >
      <fieldset className={`px-2 pb-2 lg:px-10 lg:pb-10 border-divider`}>
        <legend
          className={`text-2xl font-bold uppercase text-center pt-10`}
        >
          {title}
        </legend>
        {children && <>{children}</>}
        <ul className={`main-list list-none my-12 mx-auto max-w-7xl grid ${layoutVariant.mainListGridConfig} gap-4`}>
          <li className={`${layoutVariant.gridItemsConfig.firstName}`} key="first">
            <label htmlFor="firstname" className="sr-only">
              First name
            </label>
            <input
              type="text"
              name="firstname"
              id="firstname"
              placeholder="FIRST NAME"
              required
              className={`${inputClassBase} border px-2 bg-transparent`}
            />
          </li>
          <li className={`${layoutVariant.gridItemsConfig.lastName}`} key="last">
            <label htmlFor="lastname" className="sr-only">
              Last name
            </label>
            <input
              type="text"
              name="lasttname"
              id="lastname"
              placeholder="LAST NAME"
              required
              className={`${inputClassBase} border px-2 bg-transparent`}
            />
          </li>
          <li className={`${layoutVariant.gridItemsConfig.email}`} key="email">
            <label htmlFor="email" className="sr-only">
              Email Address
            </label>
            <input
              type="email"
              name="email"
              id="email"
              placeholder="EMAIL ADDRESS"
              required
              className={`${inputClassBase} border px-2 bg-transparent`}
            />
          </li>
          <li className={`${layoutVariant.gridItemsConfig.phone}`} key="phone">
            <label htmlFor="phone" className="sr-only">
              Phone number
            </label>
            <input
              type="tel"
              id="phone"
              name="phone"
              pattern="^([0-9]|\+|\(|\))*(\.|[0-9]| |-)*$"
              required
              placeholder="PHONE NUMBER"
              className={`${inputClassBase} border px-2 bg-transparent`}
            ></input>
          </li>
          <li
            className={`${layoutVariant.gridItemsConfig.textArea} text-center`}
            key="ita"
          >
            <label htmlFor="textarea" className="sr-only">
              {inputTextAreaLabel}
            </label>
            <textarea
              id="textarea"
              placeholder={inputTextAreaLabel}
              className="border w-full h-full px-2"
            ></textarea>
          </li>
          <li className={`${layoutVariant.gridItemsConfig.location}`} key="location">
            <label htmlFor="location" className="sr-only">
              Location
            </label>
            {locationInput}
          </li>
          <li className={`${layoutVariant.gridItemsConfig.propertyType}`} key="type">
            <label htmlFor="propertytype">
              <span className="sr-only">{propertyTypeSelectLabel}</span>
              <Select
                name="propertytype"
                value={propertyType}
                onChange={(option) => changePropertyTypeHandler(option)}
                options={propertyTypeSelectOptions}
                placeholder={propertyTypeSelectLabel}
                isMulti={false}
                styles={selectStyles}
              />
            </label>
          </li>
          <li className={`${subTypesOptions.length == 0 ? "hidden" : ""} ${layoutVariant.gridItemsConfig.propertySubType}`} key="subtype">
            <label htmlFor="propertysubtype">
              <span className="sr-only">{propertySubtypeSelectLabel}</span>
              <Select
                name="propertysubtype"
                value={subtype}
                onChange={(option) => changePropertySubtypeHandler(option)}
                options={subTypesOptions}
                placeholder={propertySubtypeSelectLabel}
                isMulti={false}
                styles={selectStyles}
              />
            </label>
          </li>
          <li className={`${layoutVariant.gridItemsConfig.beds}`} key="beds">
            <label htmlFor="beds">
              <span className="sr-only">{bedsSelect.placeholder}</span>
              <Select
                name="beds"
                value={bedsCount}
                onChange={(option) => changeBedsHandler(option)}
                options={bedsSelect.selectOptions}
                isMulti={false}
                placeholder={bedsSelect.placeholder}
                styles={selectStyles}
              />
            </label>
          </li>
          <li className={`${layoutVariant.gridItemsConfig.baths}`} key="baths">
            <label htmlFor="baths">
              <span className="sr-only">{bathsSelect.placeholder}</span>
              <Select
                name="baths"
                value={bathsCount}
                onChange={(option) => changeBathsHandler(option)}
                options={bathsSelect.selectOptions}
                isMulti={false}
                placeholder={bathsSelect.placeholder}
                styles={selectStyles}
              />
            </label>
          </li>
          <li className={`${layoutVariant.gridItemsConfig.sqft}`} key="sqft">
            <label htmlFor="sqft">
              <span className="sr-only">{squareFootageSelect.placeholder}</span>
              <Select
                name="sqft"
                value={squareFootage}
                onChange={(option) => changeSqFootageHandler(option)}
                options={squareFootageSelect.selectOptions}
                isMulti={false}
                placeholder={squareFootageSelect.placeholder}
                styles={selectStyles}
              />
            </label>
          </li>
          <li className={`${layoutVariant.gridItemsConfig.condition}`} key="condition">
            <label htmlFor="condition">
              <span className="sr-only">{conditionSelect.placeholder}</span>
              <Select
                name="condition"
                value={condition}
                onChange={(option) => changeConditionHandler(option)}
                options={conditionSelect.selectOptions}
                isMulti={false}
                placeholder={conditionSelect.placeholder}
                styles={selectStyles}
              />
            </label>
          </li>
          {layoutVariant.displayMap &&
            <li className={`${layoutVariant.mapDimension} ${layoutVariant.gridItemsConfig.map}`}>
              {center != null && formMap}
              {center == null && (
                <div className="w-full h-full flex flex-col justify-center items-center bg-slate-300">
                  Please provide a location
                </div>
              )}
            </li>
          }
          <li
            className={`${layoutVariant.gridItemsConfig.submit} text-center italic pt-3`}
            key="submit"
          >
            <button
              disabled={isSendingRequest}
              type="submit"
              value="Submit"
              className={`max-w-sm border border-body-text px-3 capitalize cursor-pointer ${inputClassBase}`}
            >
              {isSendingRequest ? (
                <>
                  <svg
                    role="status"
                    className="inline w-6 h-6 mr-2 text-gray-200 animate-spin dark:text-gray-600 fill-accent"
                    viewBox="0 0 100 101"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                      fill="currentColor"
                    />
                    <path
                      d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                      fill="currentFill"
                    />
                  </svg>
                  Loading...
                </>
              ) : (
                "Submit"
              )}
            </button>
            <div className="description mx-auto max-w-sm md:max-w-md lg:max-w-sm">
              <span className={`mb-5 block`}>
                This site is protected by reCAPTCHA see our{" "}
                <a href="/privacy">Privacy Policy</a>
              </span>
            </div>
          </li>
        </ul>
      </fieldset>
    </form>
  )
}

export default SellForm